import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  getFormattedThemeForApplying,
  getFormattedImage,
  useEventLog,
  useLogRecommendationsApiError,
} from '@bingads-webui-campaign-react/copilot-common';

import { RecommendationContext } from './recommendation-context';
import { getFeatureForAssetEvent } from './utils';

export function useCopilotRecommendations({
  resetRecommendations,
  isSignup,
  isDisplayPlus,
  isMMA,
}) {
  const activity = useContext(ActivityContext);
  const logRecommendationsAPIError = useLogRecommendationsApiError(activity);

  const [recommendationContext, setRecommendationContext] = useContext(RecommendationContext);
  const { postAssetEventLog, copilotTracking } = recommendationContext;

  const feature = getFeatureForAssetEvent({ isSignup, isDisplayPlus, isMMA });

  const { logBannerGenerated } = useEventLog(_.isFunction(postAssetEventLog) ? postAssetEventLog : _.noop);

  const handleCopilotBannerFetch = useCallback((url) => {
    resetRecommendations(url, 'copilot');
  }, [resetRecommendations]);

  const handleCopilotBannerFetchSucceeded = useCallback((url, assetsResponse) => {
    logBannerGenerated(url, assetsResponse);
    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets(assetsResponse.RecommendedAssets);
    }

    const {
      RecommendedAssets: {
        Themes,
        ShortHeadlines = [],
        LongHeadlines = [],
        Descriptions = [],
        ImageAssets = [],
      },
      Logo,
      Cta,
      IsCustomerIdInExclusionList,
    } = assetsResponse;

    const delta = {
      themes: _.map(Themes, getFormattedThemeForApplying),
      headlines: ShortHeadlines,
      longHeadlines: LongHeadlines,
      descriptions: Descriptions,
      imageAssets: _.map(ImageAssets, getFormattedImage),
      logo: Logo,
      cta: Cta,
      isCustomerIdInExclusionList: IsCustomerIdInExclusionList,
    };

    const counts = {
      feature,
      url,
      ..._.mapObject(delta, value => _.size(value)),
    };
    activity.trace(`handleCopilotBannerFetchSucceeded: counts=${JSON.stringify(counts)}`);

    setRecommendationContext(delta);
  }, [activity, copilotTracking, feature, logBannerGenerated, setRecommendationContext]);

  const handleCopilotBannerFetchFailed = logRecommendationsAPIError;

  return {
    handleCopilotBannerFetch,
    handleCopilotBannerFetchSucceeded,
    handleCopilotBannerFetchFailed,
  };
}
